import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';

let cachedPromise: Promise<Stripe | null> | null = null;

// Lazy initializer. Defers the network fetch of js.stripe.com until the first
// caller actually needs Stripe (payment page or makePayment epic), instead of
// pulling ~170 KiB into the initial bundle for every user session. The key
// check is also deferred so a missing VITE_STRIPE_KEY no longer crashes the
// whole app at boot — only payment flows fail.
export const getStripe = (): Promise<Stripe | null> => {
  if (cachedPromise) {
    return cachedPromise;
  }

  const key = import.meta.env.VITE_STRIPE_KEY;
  if (!key) {
    cachedPromise = Promise.reject(new Error('Stripe key not defined'));
    cachedPromise.catch(error => {
      Sentry.captureException(error, {
        tags: { area: 'stripe-load' },
        level: 'error',
      });
    });
    return cachedPromise;
  }

  cachedPromise = loadStripe(key);

  // Stripe loads its SDK asynchronously from js.stripe.com. The promise rejects
  // when the script is blocked (extension, network policy, captive portal) or
  // when the device drops connectivity mid-load. Without an early observer the
  // rejection surfaces as an `unhandledrejection` and pages Sentry as a crash,
  // even though the rest of the app still renders fine. Attach a passive
  // observer so the rejection is captured as a controlled, tagged exception.
  // This does not consume the rejection — components that `await getStripe()`
  // or pass it to `<Elements>` still receive it through their own chains.
  cachedPromise.catch(error => {
    Sentry.captureException(error, {
      tags: { area: 'stripe-load' },
      level: 'warning',
    });
  });

  return cachedPromise;
};
